// Note: Utils class
import { getMedical } from "../medical.js";
/**
 * Plane defined by `MedicalUtils::PlaneId` enumeration.
 */
export var PlaneId;
(function (PlaneId) {
    PlaneId[PlaneId["Coronal"] = 0] = "Coronal";
    PlaneId[PlaneId["Axial"] = 1] = "Axial";
    PlaneId[PlaneId["Sagittal"] = 2] = "Sagittal";
})(PlaneId || (PlaneId = {}));
/**
 * Utils class. Provide specific functionality for medical module.
 */
export class MedicalUtils {
    constructor(theViewer) { this._myModule = getMedical(theViewer); }
    /**
     * Polish angle to be inside the [-PI, PI] interval
     * @param theRadians the angle in radians
     * @returns angle in radians in [-PI, PI] interval
     */
    polishAngleRadians(theRadians) {
        if (theRadians < -Math.PI)
            theRadians += 2 * Math.PI;
        else if (theRadians > Math.PI)
            theRadians -= 2 * Math.PI;
        return theRadians;
    }
    /**
     * Get RI/RA Angles of object.
     * @param {string} theId id of object
     * @param {WasmVector4d} theInitialRotation initial rotation of object
     * @return {WasmVector2d} RI/RA Angles
     */
    getRiRaAngles(theId, theInitialRotation) {
        return this._myModule.utils().getRiRaAngles(theId, theInitialRotation);
    }
    /**
     * Set RI/RA Angles of object.
     * @param {string} theId id of object
     * @param {WasmVector2d} theAngles RI/RA Angles
     * @param {WasmVector4d} theInitialRotation initial rotation of object
     */
    setRiRaAngles(theId, theAngles, theInitialRotation) {
        this._myModule.utils().setRiRaAngles(theId, theAngles, theInitialRotation);
    }
    /**
     * Calculate leg length.
     * @param {WasmVector3d} thePelvisLeftASISLandmark left ASIS landmark
     * @param {WasmVector3d} thePelvisRightASISLandmark right ASIS landmark
     * @param {WasmVector3d} theLesserTrochanter the lesser trochanter position
     */
    calculateLegLength(thePelvisLeftASISLandmark, thePelvisRightASISLandmark, theLesserTrochanter) {
        return this._myModule.utils().calculateLegLength(thePelvisLeftASISLandmark, thePelvisRightASISLandmark, theLesserTrochanter);
    }
    /** Calculate projection point on plane.
     * @param {WasmVector3d} thePoint point for projection
     * @param {PlaneId} thePlaneId plane id
     */
    projectOnPlane(thePoint, thePlaneId) {
        return this._myModule.utils().projectOnPlane(thePoint, thePlaneId);
    }
    /**
     * Calculate leg acetabular offset.
     * @param {WasmVector3d} theCenterOfRotation the center of rotation.
     */
    calculateLegAcetabularOffset(theCenterOfRotation) {
        return this._myModule.utils().calculateLegAcetabularOffset(theCenterOfRotation);
    }
    /**
     * Calculate leg length.
     * @param {WasmVector3d} theFemurAxisStartLandmark femur axis start landmark.
     * @param {WasmVector3d} theFemurAxisEndLandmark femur axis end landmark.
     * @param {WasmVector3d} theCenterOfRotation the center of rotation.
     */
    calculateLegFemoralOffset(theFemurAxisStartLandmark, theFemurAxisEndLandmark, theCenterOfRotation) {
        return this._myModule.utils().calculateLegFemoralOffset(theFemurAxisStartLandmark, theFemurAxisEndLandmark, theCenterOfRotation);
    }
    /**
     * Calculate cup inclination.
     * @param {ObjectAxes} theCupAxes cup axes.
     * @param {WasmVector3d} thePelvisLeftASISLandmark left ASIS landmark
     * @param {WasmVector3d} thePelvisRightASISLandmark right ASIS landmark
     * @param {WasmVector3d} thePelvisLeftSymphysisLandmark left Symphysis landmark
     * @param {WasmVector3d} thePelvisRightSymphysisLandmark right Symphysis landmark
     */
    calculateCupInclination(theCupAxes, thePelvisLeftASISLandmark, thePelvisRightASISLandmark, thePelvisLeftSymphysisLandmark, thePelvisRightSymphysisLandmark) {
        return this._myModule.utils().calculateCupInclination(theCupAxes, thePelvisLeftASISLandmark, thePelvisRightASISLandmark, thePelvisLeftSymphysisLandmark, thePelvisRightSymphysisLandmark);
    }
    /**
     * Calculate cup version.
     * @param {ObjectAxes} theCupAxes cup axes.
     *  @param {WasmVector3d} thePelvisLeftASISLandmark left ASIS landmark
     * @param {WasmVector3d} thePelvisRightASISLandmark right ASIS landmark
     * @param {WasmVector3d} thePelvisLeftSymphysisLandmark left Symphysis landmark
     * @param {WasmVector3d} thePelvisRightSymphysisLandmark right Symphysis landmark
     */
    calculateCupVersion(theCupAxes, thePelvisLeftASISLandmark, thePelvisRightASISLandmark, thePelvisLeftSymphysisLandmark, thePelvisRightSymphysisLandmark) {
        return this._myModule.utils().calculateCupVersion(theCupAxes, thePelvisLeftASISLandmark, thePelvisRightASISLandmark, thePelvisLeftSymphysisLandmark, thePelvisRightSymphysisLandmark);
    }
    /**
     * Calculate cup orientation.
     * @param {ObjectAxes} theCupAxes cup axes.
     *  @param {WasmVector3d} thePelvisLeftASISLandmark left ASIS landmark
     * @param {WasmVector3d} thePelvisRightASISLandmark right ASIS landmark
     * @param {WasmVector3d} thePelvisLeftSymphysisLandmark left Symphysis landmark
     * @param {WasmVector3d} thePelvisRightSymphysisLandmark right Symphysis landmark
     */
    calculateCupOrientation(theCupAxes, thePelvisLeftASISLandmark, thePelvisRightASISLandmark, thePelvisLeftSymphysisLandmark, thePelvisRightSymphysisLandmark) {
        return this._myModule.utils().calculateCupOrientation(theCupAxes, thePelvisLeftASISLandmark, thePelvisRightASISLandmark, thePelvisLeftSymphysisLandmark, thePelvisRightSymphysisLandmark);
    }
    /**
      * Calculate cup transformation by cup inclination and cup version
      * @param {number} theCupInclination cup inclination angle.
      * @param {number} theCupVersion cup version angle.
      * @param {number} theCupOrientation cup orientation angle. (Used for liner orientation)
      * @param {WasmVector3d} thePelvisLeftASISLandmark left ASIS landmark
      * @param {WasmVector3d} thePelvisRightASISLandmark right ASIS landmark
      * @param {WasmVector3d} thePelvisLeftSymphysisLandmark left Symphysis landmark
      * @param {WasmVector3d} thePelvisRightSymphysisLandmark right Symphysis landmark
      *
    */
    calculateCupRotation(theCupInclination, theCupVersion, theCupOrientation, thePelvisLeftASISLandmark, thePelvisRightASISLandmark, thePelvisLeftSymphysisLandmark, thePelvisRightSymphysisLandmark) {
        return this._myModule.utils().calculateCupRotation(theCupInclination, theCupVersion, theCupOrientation, thePelvisLeftASISLandmark, thePelvisRightASISLandmark, thePelvisLeftSymphysisLandmark, thePelvisRightSymphysisLandmark);
    }
    /**
     * Calculate stem version.
     * @param {WasmVector3d} theHeadEndpoint stem head endpoint.
     * @param {WasmVector3d} theStemNeck stem neck point.
     * @param {WasmVector3d} theFemurCondyleLat femur condyle lat landmark.
     * @param {WasmVector3d} theFemurCondyleMed femur condyle med landmark.
     * @param {WasmVector3d} theSuperiorAxis superior axis defining reference plane.
     */
    calculateStemVersion(theHeadEndpoint, theStemNeck, theFemurCondyleLat, theFemurCondyleMed, theSuperiorAxis) {
        return this._myModule.utils().calculateStemVersion(theHeadEndpoint, theStemNeck, theFemurCondyleLat, theFemurCondyleMed, theSuperiorAxis);
    }
    /**
     * Calculate neck cut height.
     * @param {WasmVector3d} theLesserTrochanter the lesser trochanter position.
     * @param {WasmPlane3d} theResectionPln the neck resection plane.
     */
    calculateNeckCutHeight(theLesserTrochanter, theResectionPln) {
        return this._myModule.utils().calculateNeckCutHeight(theLesserTrochanter, theResectionPln);
    }
    /**
     * Calculate relative rotation.
     * @param {WasmVector3d} theCenterOfRotation center of rotation.
     * @param {WasmVector4d} theRotation rotation quaternion.
     * @param {WasmMatrix4d} theInitilalTransform initial transformation of object to rotate.
     * @return {WasmMatrix4d} new transformation of object.
     */
    calculateRelativeRotation(theCenterOfRotation, theRotation, theInitilalTransform) {
        return this._myModule.utils().calculateRelativeRotation(theCenterOfRotation, theRotation, theInitilalTransform);
    }
    /**
     * Calculate stem rotation.
     * @param {number} theStemVersion  stem version.
     * @param {OperationSide} theOperationSide stem neck point.
     * @param {ObjectAxes} theStemAxes stem axes.
     * @param {WasmVector3d} theHeadEndpoint stem head endpoint.
     * @param {WasmVector3d} theStemNeck stem neck point.
     * @param {WasmVector3d} theFemurCondyleLat femur condyle lat landmark.
     * @param {WasmVector3d} theFemurCondyleMed femur condyle med landmark.
     * @param {WasmVector3d} theSuperiorAxis superior axis defining reference plane normal.
     * @return {WasmMatrix4d} stem rotation matrix.
     */
    calculateStemRotation(theStemVersion, theStemAxes, theHeadEndpoint, theStemNeck, theFemurCondyleLat, theFemurCondyleMed, theSuperiorAxis) {
        return this._myModule.utils().calculateStemRotation(theStemVersion, theStemAxes, theHeadEndpoint, theStemNeck, theFemurCondyleLat, theFemurCondyleMed, theSuperiorAxis);
    }
    /**
     * Get unpacked json with collision data.
     * @param {string} theFilePath path to gzip.
     * @return {string} unpacked json with collision data.
     */
    unpackCollisionData(theFilePath) {
        return this._myModule.utils().unpackCollisionData(theFilePath);
    }
    /**
     * Unpack json from gzip buffer.
     * @param {string}      theName     json name
     * @param {number} theBuffer   pointer to data
     * @param {number}      theDataLen  data length
     * @return {string} unpacked json with collision data.
     */
    unpackCollisionDataFromMemory(theName, theBuffer, theDataLen) {
        //@ts-ignore
        return this._myModule.utils().unpackCollisionDataFromMemory(theName, theBuffer, theDataLen);
    }
    /**
     * Rotate camera if needed.
     * @param {WasmVector3d} theReferenceDir reference up.
     * @param {WasmVector3d} theReferenceRight reference right.
     * @param {WasmVector3d} theNewDir new normal.
     * @param {WasmVector3d} theNewRight new right.
     * @param {WasmVector3d} theRotationPoint rotation point.
     * @param {WasmMatrix4d} theOldTransform old transform.
     * @param {WasmMatrix4d} theNewTransform new transform.
     * @param {Camera} theCamera camera.
     */
    rotateCameraIfNeeded(theReferenceDir, theReferenceRight, theNewDir, theNewRight, theRotationPoint, theOldTransform, theNewTransform, theCamera) {
        return this._myModule.utils().rotateCameraIfNeeded(theReferenceDir, theReferenceRight, theNewDir, theNewRight, theRotationPoint, theOldTransform, theNewTransform, theCamera.viewId);
    }
}
